import React, { useState, useEffect, useMemo, useContext } from "react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  DrawerClose,
  DrawerBody,
} from "components/Graphs/Drawer";
import { Button } from "components/Graphs/Button";
import { Input } from "components/Graphs/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/Graphs/Select";
import { Textarea } from "components/Graphs/TextArea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "components/Graphs/Tabs";
import {
  PlusCircle,
  Trash2,
  Edit2,
  Save,
  X,
  AlertTriangle,
  PlusCircleIcon,
  Loader2,
} from "lucide-react";
import { RadioGroup, RadioGroupItem } from "components/Graphs/RadioGroup";
import {
  CloudPolicy,
  CloudQuota,
  CreateCloudQuota,
  CloudProvider,
  PolicyContext,
  DefaultRule,
  getDefaultRuleString,
  getDefaultRuleEnum,
  UpdateCloudPolicyDefaultRuleRequest,
  AddQuotasToCloudPolicyRequest,
  UpdateCloudQuotaRequest,
  CloudQuotaUtilization,
} from "components/CloudPolicy/types";
import { DEFAULT_RULE_OPTIONS } from "./CreatePolicy";
import { Badge } from "components/Graphs/Badge";

import { ProgressBar } from "components/Graphs/ProgressBar";
import { FlatCard, Spinner } from "components/UI-lib";
import { PencilIcon } from "@heroicons/react/24/outline";
import agent from "server";
import { NotificationContext } from "contexts/NotificationContext";

interface EditPolicySheetProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  policy: CloudPolicy | null;
  onUpdatePolicy: (policy: CloudPolicy) => void;
  organizationId: string; // Not used for editing existing, but good for context
  cloudProviders: CloudProvider[];
}

interface QuotaWithType extends CloudQuota {
  quotaType?: "instance" | "gpu";
}

const QuotaForm = ({
  quota,
  onQuotaChange,
  onCancel,
  errorMessage,
  isNew = false,
}: {
  quota: QuotaWithType;
  onQuotaChange: (updatedQuota: QuotaWithType) => void;
  onCancel: () => void;
  isNew?: boolean;
  errorMessage: string | null;
}) => {
  const [currentQuota, setCurrentQuota] = useState<QuotaWithType>(quota);

  useEffect(() => {
    setCurrentQuota(quota);
  }, [quota]);

  const handleChange = (field: keyof QuotaWithType, value: string | number) => {
    setCurrentQuota((prev) => {
      const newQuota = {
        ...prev,
        [field]:
          field === "limit"
            ? value === ""
              ? undefined
              : typeof value === "string"
              ? isNaN(Number(value))
                ? undefined
                : Number(value)
              : value
            : value,
      };

      // When changing quotaType, clear the other type
      if (field === "quotaType") {
        if (value === "instance") {
          newQuota.gpu_type = "";
        } else {
          newQuota.instance_type = "";
        }
      }

      return newQuota;
    });
  };

  if (isNew) {
    // Simple form for new quotas (matches CreatePolicy.tsx)
    return (
      <div className="grid grid-cols-1 gap-4 p-4 border border-zinc-800 rounded-md relative bg-secondaryBg">
        <div className="flex flex-col items-start gap-4">
          <div className="w-full">
            <label className="text-white text-sm font-semibold mb-2 block">
              Select a Resource Type
            </label>
            <RadioGroup
              value={currentQuota.quotaType}
              onValueChange={(value) => handleChange("quotaType", value)}
              className="flex flex-row gap-x-3"
            >
              <div className="flex items-center gap-x-3">
                <RadioGroupItem value="instance" id="instance-new" />
                <label htmlFor="instance-new" className="text-sm text-white">
                  Instance Type
                </label>
              </div>
              <div className="flex items-center gap-x-3">
                <RadioGroupItem value="gpu" id="gpu-new" />
                <label htmlFor="gpu-new" className="text-sm text-white">
                  GPU Type
                </label>
              </div>
            </RadioGroup>
          </div>

          <div className="gap-2 w-full">
            <label htmlFor="quota-type-new" className="text-white text-sm">
              {currentQuota.quotaType === "instance"
                ? "Instance Type"
                : "GPU Type"}
            </label>
            <Input
              id="quota-type-new"
              value={
                currentQuota.quotaType === "instance"
                  ? currentQuota.instance_type || ""
                  : currentQuota.gpu_type || ""
              }
              onChange={(e) =>
                handleChange(
                  currentQuota.quotaType === "instance"
                    ? "instance_type"
                    : "gpu_type",
                  e.target.value
                )
              }
              placeholder={
                currentQuota.quotaType === "instance"
                  ? "e.g., m5.large, n1-standard-1"
                  : "e.g., T4, V100, A100"
              }
            />
          </div>

          <div className="gap-2 w-full">
            <label htmlFor="quota-limit-new" className="text-white text-sm">
              Limit
            </label>
            <Input
              id="quota-limit-new"
              type="number"
              value={currentQuota.limit ?? ""}
              onChange={(e) => handleChange("limit", e.target.value)}
              placeholder="e.g., 10"
            />
          </div>
        </div>
        <div className="flex flex-col justify-end gap-2">
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={() => onQuotaChange(currentQuota)}>
              <Save className="mr-2 h-4 w-4" /> Add Quota
            </Button>
          </div>
          {errorMessage && (
            <div className="flex flex-row justify-end">
              <p className="text-red-500 text-sm">{errorMessage}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Full form for editing existing quotas - matches CreatePolicy style exactly
  return (
    <div className="grid grid-cols-1 gap-4 p-4 border border-zinc-800 rounded-md relative bg-secondaryBg">
      <div className="flex flex-col items-start gap-4">
        <div className="flex flex-row items-center justify-between text-primary">
          <PencilIcon className="h-4 w-4 mr-2" />
          <span className="text-sm font-semibold">Editing Quota</span>
        </div>
        <hr className="w-[100%] dark:border dark:border-zinc-800" />
        <div className="w-full">
          <label className="text-white text-sm font-semibold mb-2 block">
            Select a Resource Type
          </label>
          <RadioGroup
            value={currentQuota.quotaType}
            onValueChange={(value) => handleChange("quotaType", value)}
            className="flex flex-row gap-x-3"
          >
            <div className="flex items-center gap-x-3">
              <RadioGroupItem value="instance" id="instance-edit" />
              <label htmlFor="instance-edit" className="text-sm text-white">
                Instance Type
              </label>
            </div>
            <div className="flex items-center gap-x-3">
              <RadioGroupItem value="gpu" id="gpu-edit" />
              <label htmlFor="gpu-edit" className="text-sm text-white">
                GPU Type
              </label>
            </div>
          </RadioGroup>
        </div>

        <div className="gap-2 w-full">
          <label htmlFor="quota-type-edit" className="text-white text-sm">
            {currentQuota.quotaType === "instance"
              ? "Instance Type"
              : "GPU Type"}
          </label>
          <Input
            id="quota-type-edit"
            value={
              currentQuota.quotaType === "instance"
                ? currentQuota.instance_type || ""
                : currentQuota.gpu_type || ""
            }
            onChange={(e) =>
              handleChange(
                currentQuota.quotaType === "instance"
                  ? "instance_type"
                  : "gpu_type",
                e.target.value
              )
            }
            placeholder={
              currentQuota.quotaType === "instance"
                ? "e.g., m5.large, n1-standard-1"
                : "e.g., T4, V100, A100"
            }
          />
        </div>

        <div className="gap-2 w-full">
          <label htmlFor="quota-limit-edit" className="text-white text-sm">
            Limit
          </label>
          <Input
            id="quota-limit-edit"
            type="number"
            value={currentQuota.limit ?? ""}
            onChange={(e) => handleChange("limit", e.target.value)}
            placeholder="e.g., 10"
          />
        </div>
      </div>
      <div className="flex flex-col justify-end gap-2 pt-2">
        <div className="flex flex-col gap-2 w-full">
          <div className="flex flex-row gap-2 w-full justify-end">
            <Button variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={() => onQuotaChange(currentQuota)}>
              <Save className="mr-2 h-4 w-4" /> Save Changes
            </Button>
          </div>
          {errorMessage && (
            <div className="flex flex-row justify-end">
              <p className="text-red-500 text-sm">{errorMessage}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function EditPolicy({
  isOpen,
  onOpenChange,
  policy,
  onUpdatePolicy,
  organizationId,
  cloudProviders,
}: EditPolicySheetProps) {
  const [editedPolicy, setEditedPolicy] = useState<CloudPolicy>(
    JSON.parse(JSON.stringify(policy))
  ); // Deep copy
  const [currentTag, setCurrentTag] = useState("");
  const [editingQuota, setEditingQuota] = useState<QuotaWithType | null>(null);
  const [isAddingNewQuota, setIsAddingNewQuota] = useState(false);
  const [tabsValue, setTabsValue] = useState("settings");
  const notificationContext = useContext(NotificationContext);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorMessageSettings, setErrorMessageSettings] = useState<
    string | null
  >(null);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    setEditedPolicy(JSON.parse(JSON.stringify(policy))); // Deep copy when policy prop changes
    setEditingQuota(null);
    setIsAddingNewQuota(false);
  }, [policy, isOpen]);

  const handleDefaultRuleChange = (value: string) => {
    setEditedPolicy((prev) => ({
      ...prev,
      default_rule: getDefaultRuleEnum(value),
    }));
  };

  const handleQuotaUpdate = async (updatedQuotaData: QuotaWithType) => {
    setErrorMessage(null);
    // Validate before API call
    const limitValue = String(updatedQuotaData.limit).trim();
    if (
      limitValue === "" ||
      Number(limitValue) < 0 ||
      isNaN(Number(limitValue))
    ) {
      setErrorMessage("Limit must be a positive number.");
      return;
    }

    if (!updatedQuotaData.quotaType) {
      setErrorMessage("Please select Instance Type or GPU Type.");
      return;
    }

    if (
      updatedQuotaData.quotaType === "instance" &&
      !updatedQuotaData.instance_type?.trim()
    ) {
      setErrorMessage("Instance Type is required.");
      return;
    }

    if (
      updatedQuotaData.quotaType === "gpu" &&
      !updatedQuotaData.gpu_type?.trim()
    ) {
      setErrorMessage("GPU Type is required.");
      return;
    }

    try {
      if (isAddingNewQuota) {
        // Add new quota via API
        const request: AddQuotasToCloudPolicyRequest = {
          quotas: [
            {
              instance_type:
                updatedQuotaData.quotaType === "instance"
                  ? updatedQuotaData.instance_type
                  : "",
              gpu_type:
                updatedQuotaData.quotaType === "gpu"
                  ? updatedQuotaData.gpu_type
                  : "",
              limit: updatedQuotaData.limit!,
            },
          ],
        };

        const res = await agent.Admin.addQuotasToCloudPolicy(
          organizationId,
          editedPolicy.id!,
          request
        );

        if (res.success && res.data) {
          const updatedPolicy = (res.data as any).cloud_policy || res.data;
          setEditedPolicy(updatedPolicy);
          onUpdatePolicy(updatedPolicy);
        } else {
          alert(res.message || "Failed to add quota");
          return;
        }
      } else if (editingQuota && (editingQuota as CloudQuota).id) {
        // Update existing quota via API
        const request: UpdateCloudQuotaRequest = {
          instance_type:
            updatedQuotaData.quotaType === "instance"
              ? updatedQuotaData.instance_type
              : "",
          gpu_type:
            updatedQuotaData.quotaType === "gpu"
              ? updatedQuotaData.gpu_type
              : "",
          limit: updatedQuotaData.limit,
        };

        const res = await agent.Admin.updateCloudQuota(
          organizationId,
          editedPolicy.id!,
          (editingQuota as CloudQuota).id!,
          request
        );

        if (res.success && res.data && res.data.quota) {
          const updatedQuota = res.data.quota;
          const currentPolicy = editedPolicy;
          currentPolicy.quotas = currentPolicy.quotas?.map((q) =>
            q.id === updatedQuota.id ? updatedQuota : q
          );

          setEditedPolicy(currentPolicy);
          onUpdatePolicy(currentPolicy);
        } else {
          setErrorMessage(res.message || "Failed to update quota");
          return;
        }
      }

      setEditingQuota(null);
      setIsAddingNewQuota(false);
    } catch (error) {
      setErrorMessage("An error occurred while updating the quota");
      console.error("Quota update error:", error);
    }
  };

  const handleEditQuota = (quotaToEdit: CloudQuota) => {
    // Determine quotaType based on existing data
    const quotaType = quotaToEdit.instance_type ? "instance" : "gpu";
    setEditingQuota({ ...quotaToEdit, quotaType });
    setIsAddingNewQuota(false);
  };

  const handleAddNewQuota = () => {
    setEditingQuota({
      instance_type: "",
      gpu_type: "",
      limit: undefined,
      quotaType: "instance", // Default to instance type
    });
    setIsAddingNewQuota(true);
  };

  const handleRemoveQuota = async (quotaIdToRemove?: string) => {
    if (!quotaIdToRemove) return;

    try {
      const res = await agent.Admin.deleteCloudQuota(
        organizationId,
        editedPolicy.id!,
        quotaIdToRemove
      );

      if (res.success) {
        // Update local state to remove the quota
        setEditedPolicy((prev) => ({
          ...prev,
          quotas: prev.quotas?.filter((q) => q.id !== quotaIdToRemove),
        }));

        // Update parent component
        const updatedPolicy = {
          ...editedPolicy,
          quotas: editedPolicy.quotas?.filter((q) => q.id !== quotaIdToRemove),
        };
        onUpdatePolicy(updatedPolicy);
      } else {
        notificationContext.showNotification(
          "Failed to delete quota",
          res.message || "Failed to delete quota",
          "error"
        );
      }
    } catch (error) {
      notificationContext.showNotification(
        "Failed to delete quota",
        "An error occurred while deleting the quota",
        "error"
      );
      console.error("Quota deletion error:", error);
    }
  };

  const handleSaveSettings = async () => {
    setErrorMessageSettings(null);
    setSavingSettings(true);
    try {
      // Update default rule via API
      const request: UpdateCloudPolicyDefaultRuleRequest = {
        default_rule: getDefaultRuleString(editedPolicy.default_rule),
      };

      const res = await agent.Admin.updateCloudPolicyDefaultRule(
        organizationId,
        editedPolicy.id!,
        request
      );
      setSavingSettings(false);
      if (res.success && res.data) {
        const updatedPolicy = (res.data as any).cloud_policy || res.data;
        setEditedPolicy(updatedPolicy);
        onUpdatePolicy(updatedPolicy);
        onOpenChange(false);
      } else {
        setErrorMessageSettings(
          res.message || "Failed to update policy settings"
        );
      }
    } catch (error) {
      setSavingSettings(false);
      setErrorMessageSettings(
        "An error occurred while updating the policy settings"
      );
      console.error("Policy settings update error:", error);
    }
  };

  const providerInfo = useMemo(() => {
    return cloudProviders.find(
      (provider) => provider.id === editedPolicy?.cloud_cred_id
    );
  }, [cloudProviders, editedPolicy?.cloud_cred_id]);

  return (
    <Drawer open={isOpen} onOpenChange={onOpenChange}>
      {policy && editedPolicy && (
        <DrawerContent className="sm:max-w-lg w-[90vw] overflow-y-auto">
          <DrawerHeader>
            <DrawerTitle>
              Manage Policy: {editedPolicy.context?.name}
            </DrawerTitle>
            <div className="text-sm text-secondary">
              Modify settings and quotas for this policy on {providerInfo?.name}{" "}
              ({editedPolicy.cloud_cred_id}).
            </div>
          </DrawerHeader>
          <DrawerBody>
            <Tabs
              value={tabsValue}
              onValueChange={setTabsValue}
              className="flex-grow flex flex-col overflow-hidden"
            >
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="settings">Policy Settings</TabsTrigger>
                <TabsTrigger value="quotas">
                  Quotas ({editedPolicy.quotas?.length || 0})
                </TabsTrigger>
              </TabsList>

              <TabsContent
                value="settings"
                className="flex-grow overflow-y-auto space-y-3 pr-2"
              >
                <div className="grid gap-1">
                  <label className="text-sm text-primary">Policy Name:</label>
                  <p className="text-sm text-secondary">
                    {editedPolicy.context?.name || "Unnamed Policy"}
                  </p>
                </div>
                <div className="flex flex-row gap-2">
                  <Badge variant="neutral">{providerInfo?.name}</Badge>
                </div>
                <div className="grid gap-2">
                  <label className="text-sm text-primary">Description</label>
                  <p className="text-sm text-secondary">
                    {editedPolicy.context?.description ||
                      "No description provided"}
                  </p>
                </div>
                <div className="grid gap-2">
                  <label className="text-sm text-primary">Tags</label>
                  <div className="flex flex-wrap gap-2">
                    {editedPolicy.context?.tags &&
                    editedPolicy.context.tags.length > 0 ? (
                      editedPolicy.context.tags.map((tag) => (
                        <Badge key={tag} variant="neutral">
                          {tag}
                        </Badge>
                      ))
                    ) : (
                      <p className="text-sm text-secondary">No tags assigned</p>
                    )}
                  </div>
                </div>
                <div className="grid gap-2">
                  <label
                    htmlFor="edit-defaultRule"
                    className="text-sm text-primary"
                  >
                    Default Rule
                  </label>
                  <Select
                    value={getDefaultRuleString(editedPolicy.default_rule)}
                    onValueChange={handleDefaultRuleChange}
                  >
                    <SelectTrigger id="edit-defaultRule">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DEFAULT_RULE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>

              <TabsContent
                value="quotas"
                className="flex-grow overflow-y-auto pr-1"
              >
                {editingQuota || isAddingNewQuota ? (
                  <QuotaForm
                    errorMessage={errorMessage}
                    quota={editingQuota!}
                    onQuotaChange={handleQuotaUpdate}
                    onCancel={() => {
                      setEditingQuota(null);
                      setIsAddingNewQuota(false);
                    }}
                    isNew={isAddingNewQuota}
                  />
                ) : (
                  <>
                    <Button
                      onClick={handleAddNewQuota}
                      variant="primary"
                      className="w-full mb-4"
                    >
                      <PlusCircleIcon className="mr-2 h-4 w-4" /> Add New Quota
                    </Button>
                    {(!editedPolicy.quotas ||
                      editedPolicy.quotas?.length === 0) && (
                      <div className="text-center py-8 text-secondary border border-dashed rounded-md">
                        <AlertTriangle className="mx-auto h-8 w-8 mb-2" />
                        <p className="text-secondary">
                          No quotas defined for this policy.
                        </p>
                        <p className="text-xs text-secondary">
                          Click "Add New Quota" to get started.
                        </p>
                      </div>
                    )}
                    <div className="space-y-3">
                      {editedPolicy.quotas?.map((q) => (
                        <FlatCard key={q.id} className="overflow-hidden">
                          <div className="flex flex-row items-center justify-between">
                            <div className="flex flex-col">
                              <div className="text-sm font-medium truncate text-primary">
                                {q.instance_type
                                  ? `Instance Type: ${q.instance_type}`
                                  : `GPU: ${q.gpu_type}`}
                              </div>
                              <div className="text-xs text-secondary">
                                Limit: {q.limit || 0}
                              </div>
                            </div>

                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                onClick={() => handleEditQuota(q)}
                              >
                                <Edit2 className="h-4 w-4" />{" "}
                                <span className="sr-only">Edit</span>
                              </Button>

                              <Button
                                variant="ghost"
                                className="text-red-500 hover:text-red-600"
                                onClick={() => handleRemoveQuota(q.id)}
                              >
                                <Trash2 className="h-4 w-4" />{" "}
                                <span className="sr-only">Delete</span>
                              </Button>
                            </div>
                          </div>
                          <hr className="w-[100%] mb-3 mt-3 dark:border dark:border-zinc-800" />
                          {q.limit ? (
                            <div className="p-3 text-xs space-y-1.5">
                              <CloudQuotaUtilizationComponent
                                orgId={organizationId}
                                policyId={editedPolicy.id!}
                                quotaId={q.id!}
                                limit={q.limit!}
                              />
                            </div>
                          ) : (
                            <>
                              <div className="p-3 text-xs bg-secondaryBg">
                                Quota is set to 0. User's will NOT be able to
                                see or provision this GPU.
                              </div>
                            </>
                          )}
                        </FlatCard>
                      ))}
                    </div>
                  </>
                )}
              </TabsContent>
            </Tabs>
          </DrawerBody>

          <DrawerFooter className="mt-auto pt-6">
            <div className="flex flex-col gap-2">
              <div className="flex flex-row gap-2">
                <DrawerClose asChild>
                  <Button variant="secondary">Cancel</Button>
                </DrawerClose>
                {tabsValue === "settings" && (
                  <>
                    {savingSettings ? (
                      <div className="flex flex-row h-8 w-8 items-center justify-center">
                        <Spinner type="primary" />
                      </div>
                    ) : (
                      <Button
                        onClick={handleSaveSettings}
                        disabled={
                          getDefaultRuleString(editedPolicy.default_rule) ===
                          getDefaultRuleString(policy?.default_rule)
                        }
                      >
                        <Save className="mr-2 h-4 w-4" /> Save Changes
                      </Button>
                    )}
                  </>
                )}
              </div>
              {errorMessageSettings && (
                <p className="text-red-500 text-sm">{errorMessageSettings}</p>
              )}
            </div>
          </DrawerFooter>
        </DrawerContent>
      )}
    </Drawer>
  );
}

export const CloudQuotaUtilizationComponent = ({
  orgId,
  policyId,
  quotaId,
  limit,
}: {
  orgId: string;
  policyId: string;
  quotaId: string;
  limit: number;
}) => {
  const [quotaUtilization, setQuotaUtilization] =
    useState<CloudQuotaUtilization | null>(null);

  useEffect(() => {
    const fetchQuotaUtilization = async () => {
      const res = await agent.Admin.getCloudQuotaUtilization(
        orgId,
        policyId,
        quotaId
      );
      if (res.success && res.data && res.data.quota_utilization) {
        setQuotaUtilization(res.data.quota_utilization);
      }
    };
    fetchQuotaUtilization();
  }, [orgId, policyId, quotaId]);

  return (
    <div>
      <div className="flex justify-between mb-0.5">
        <span className="font-medium">
          Usage: {quotaUtilization?.current || 0} / {limit}
        </span>
        <span className="text-secondary">
          {limit
            ? (((quotaUtilization?.current || 0) / limit) * 100).toFixed(0)
            : 0}
          %
        </span>
      </div>
      <ProgressBar
        value={limit ? ((quotaUtilization?.current || 0) / limit) * 100 : 0}
        className="h-1.5"
        variant="nvidia"
      />
    </div>
  );
};
